import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Image as ImageIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import RichTextEditor from './RichTextEditor';

const SECTION_BACKGROUND_PALETTE = [
  { label: 'White', value: '#FFFFFF' },
  { label: 'Warm', value: '#FFF8F1' },
  { label: 'Cream', value: '#FFFDF7' },
  { label: 'Mist', value: '#F8FBFF' },
  { label: 'Sky', value: '#EEF6FF' },
  { label: 'Sand', value: '#F8F1E7' },
  { label: 'Rose', value: '#FFF1F2' },
  { label: 'Navy', value: '#0F2747' },
];

const SectionEditorDialog = ({
  open,
  onClose,
  onSave,
  saving,
  sectionDraft,
  sectionDraftTabs,
  setSectionEditor,
  showSnackbar,
  availablePageLinks,
  openImageDialogFor,
  sectionLibrary,
  getSectionTemplate,
  getSectionTypeOptions,
  normalizeSection,
  clampNumber,
  normalizeHexColor,
  normalizeStringList,
  normalizeDropdownOptions,
  normalizeTableColumns,
  normalizeTableRows,
  parseTableColumnsInput,
  parseTableRowsInput,
  serializeTableRows,
  serializeIconCards,
  parseIconCardsInput,
  serializeCareerCards,
  parseCareerCardsInput,
  serializeSocialLinks,
  parseSocialLinksInput,
  serializeResultCards,
  parseResultCardsInput,
  serializeProfileCards,
  parseFacultyCardsInput,
  parseFounderCardsInput,
  parseProfileCardsInput,
  serializeStatsItems,
  parseStatsItemsInput,
  serializeSidebarItems,
  parseSidebarItemsInput,
  constants,
}) => {
  const {
    SECTION_TITLE_FONT_MIN,
    SECTION_TITLE_FONT_MAX,
    SECTION_TITLE_FONT_DEFAULT,
    SECTION_TITLE_COLOR_DEFAULT,
    SECTION_TITLE_BG_DEFAULT,
    PROFILE_IMAGE_SIZE_MIN,
    PROFILE_IMAGE_SIZE_MAX,
    PROFILE_IMAGE_SIZE_DEFAULT,
    MEDIA_HEIGHT_MIN,
    MEDIA_HEIGHT_MAX,
    MEDIA_HEIGHT_DEFAULT,
    HERO_HEIGHT_MIN,
    HERO_HEIGHT_MAX,
    HERO_HEIGHT_DEFAULT,
    HERO_CONTENT_WIDTH_MIN,
    HERO_CONTENT_WIDTH_MAX,
    HERO_CONTENT_WIDTH_DEFAULT,
    SECTION_WIDTH_MIN,
    SECTION_WIDTH_MAX,
    SECTION_WIDTH_DEFAULT,
    COMPONENT_MARGIN_MIN,
    COMPONENT_MARGIN_MAX,
    COMPONENT_MARGIN_DEFAULT,
    SECTION_BG_DEFAULT,
  } = constants;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1.5,
          border: '1px solid rgba(59, 130, 246, 0.14)',
          boxShadow: '0 28px 64px rgba(15, 23, 42, 0.16)',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 1.2,
          px: 1.7,
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
          background:
            'linear-gradient(135deg, rgba(37,99,235,0.12) 0%, rgba(244,124,60,0.12) 52%, rgba(255,255,255,0.96) 100%)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={0.2}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0F172A' }}>
              Customize Section
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(15,23,42,0.72)' }}>
              Keep it simple: update content, tweak style, then save.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label="Text First"
              sx={{ fontWeight: 700, bgcolor: 'rgba(37,99,235,0.1)', color: '#1D4ED8', borderRadius: 99 }}
            />
            <Chip
              size="small"
              label="Style Optional"
              sx={{ fontWeight: 700, bgcolor: 'rgba(244,124,60,0.14)', color: '#C2410C', borderRadius: 99 }}
            />
            <Chip
              size="small"
              label="Save Live"
              sx={{ fontWeight: 700, bgcolor: 'rgba(22,163,74,0.12)', color: '#15803D', borderRadius: 99 }}
            />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 1.5,
          pb: 1.1,
          px: 1.7,
          background: 'linear-gradient(180deg, rgba(248,251,255,0.86) 0%, rgba(255,255,255,0.98) 24%)',
          '& .MuiPaper-root.MuiPaper-outlined': {
            borderRadius: 2,
            borderColor: 'rgba(15,23,42,0.08)',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.8,
            background: '#FFFFFF',
          },
          '& .MuiButton-outlined': {
            borderRadius: 1.8,
          },
          '& .MuiButton-contained': {
            borderRadius: 1.8,
          },
          '& .MuiSlider-root': {
            color: '#2563EB',
          },
        }}
      >
        {sectionDraft && (
          <Stack spacing={1.55}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.15,
                background: 'linear-gradient(135deg, rgba(37,99,235,0.05) 0%, rgba(244,124,60,0.08) 100%)',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      mb: 0.25,
                    }}
                  >
                    Quick Flow
                  </Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#0F172A', lineHeight: 1.5 }}>
                    Edit content first. Use design controls only when you need a visual change.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label="1. Content" sx={{ fontWeight: 700, borderRadius: 99, bgcolor: '#DBEAFE', color: '#1D4ED8' }} />
                  <Chip size="small" label="2. Design" sx={{ fontWeight: 700, borderRadius: 99, bgcolor: '#FFEDD5', color: '#C2410C' }} />
                  <Chip size="small" label="3. Save" sx={{ fontWeight: 700, borderRadius: 99, bgcolor: '#DCFCE7', color: '#15803D' }} />
                </Stack>
              </Stack>
            </Paper>

            <Grid container spacing={1.2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Section Type"
                  value={sectionDraft.type || 'text'}
                  onChange={(event) => {
                    const nextType = event.target.value;
                    const template = getSectionTemplate(nextType);
                    setSectionEditor((prev) => ({
                      ...prev,
                      draft: normalizeSection({
                        ...(prev.draft || {}),
                        type: nextType,
                        title: prev.draft?.title || template.defaults.title,
                        body: prev.draft?.body || template.defaults.body,
                        tabs: prev.draft?.tabs || template.defaults.tabs,
                        image: prev.draft?.image || template.defaults.image,
                        videoUrl: prev.draft?.videoUrl || template.defaults.videoUrl,
                      }),
                    }));
                  }}
                >
                  {getSectionTypeOptions(sectionLibrary, sectionDraft?.type).map((componentType) => (
                    <MenuItem key={componentType.id} value={componentType.id}>
                      {componentType.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                {sectionDraft.type === 'divider' ? (
                  <TextField
                    size="small"
                    fullWidth
                    label="Divider Label (optional)"
                    value={sectionDraft.dividerLabel || ''}
                    onChange={(event) =>
                      setSectionEditor((prev) => ({
                        ...prev,
                        draft: { ...(prev.draft || {}), dividerLabel: event.target.value },
                      }))
                    }
                  />
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Heading"
                    value={sectionDraft.title || ''}
                    onChange={(event) =>
                      setSectionEditor((prev) => ({
                        ...prev,
                        draft: { ...(prev.draft || {}), title: event.target.value },
                      }))
                    }
                  />
                )}
              </Grid>
            </Grid>

            {sectionDraft.type !== 'tabs' &&
              sectionDraft.type !== 'experience_story' &&
              sectionDraft.type !== 'divider' && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.6, display: 'block' }}>
                    Main Content
                  </Typography>
                  <RichTextEditor
                    value={sectionDraft.body || ''}
                    onChange={(nextHtml) =>
                      setSectionEditor((prev) => ({
                        ...prev,
                        draft: { ...(prev.draft || {}), body: nextHtml },
                      }))
                    }
                    placeholder="Use toolbar for bold, italic, links, colors, lists..."
                  />
                </Box>
              )}

            {(sectionDraft.type === 'tabs' || sectionDraft.type === 'experience_story') && (
              <Stack spacing={1}>
                {sectionDraftTabs.map((tabItem, tabIndex) => (
                  <Paper key={`section-editor-tab-${tabIndex}`} variant="outlined" sx={{ p: 1.2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label={`Tab ${tabIndex + 1} Title`}
                        value={tabItem.title}
                        onChange={(event) => {
                          const nextTabs = sectionDraftTabs.map((item, idx) =>
                            idx === tabIndex ? { ...item, title: event.target.value } : item
                          );
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), tabs: nextTabs },
                          }));
                        }}
                      />
                      <IconButton
                        color="error"
                        onClick={() => {
                          if (sectionDraftTabs.length <= 1) {
                            showSnackbar('At least one tab is required.', 'warning');
                            return;
                          }
                          const nextTabs = sectionDraftTabs.filter((_, idx) => idx !== tabIndex);
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), tabs: nextTabs },
                          }));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <RichTextEditor
                      value={tabItem.content}
                      onChange={(nextHtml) => {
                        const nextTabs = sectionDraftTabs.map((item, idx) =>
                          idx === tabIndex ? { ...item, content: nextHtml } : item
                        );
                        setSectionEditor((prev) => ({
                          ...prev,
                          draft: { ...(prev.draft || {}), tabs: nextTabs },
                        }));
                      }}
                      placeholder="Tab content with HTML formatting..."
                    />
                  </Paper>
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setSectionEditor((prev) => ({
                      ...prev,
                      draft: {
                        ...(prev.draft || {}),
                        tabs: [
                          ...sectionDraftTabs,
                          { title: `Tab ${sectionDraftTabs.length + 1}`, content: '<p>Add content here.</p>' },
                        ],
                      },
                    }))
                  }
                >
                  Add Tab
                </Button>
              </Stack>
            )}

            {sectionDraft.type === 'experience_story' && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.6, display: 'block' }}>
                  Narrative Content (Right Side)
                </Typography>
                <RichTextEditor
                  value={sectionDraft.body || ''}
                  onChange={(nextHtml) =>
                    setSectionEditor((prev) => ({
                      ...prev,
                      draft: { ...(prev.draft || {}), body: nextHtml },
                    }))
                  }
                  placeholder="Add the main editorial description for the right-side story block..."
                />
              </Box>
            )}

            {sectionDraft.type !== 'divider' ? (
              <>
                {sectionDraft.type !== 'hero' && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(sectionDraft.buttonEnabled)}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), buttonEnabled: event.target.checked },
                            }))
                          }
                        />
                      }
                      label="Show Button"
                    />
                    <Grid container spacing={1.2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Button Text"
                          value={sectionDraft.buttonLabel || ''}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), buttonLabel: event.target.value },
                            }))
                          }
                          disabled={!sectionDraft.buttonEnabled}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Button Link"
                          value={sectionDraft.buttonPath || ''}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), buttonPath: event.target.value },
                            }))
                          }
                          placeholder="/page/custom or https://example.com"
                          disabled={!sectionDraft.buttonEnabled}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1.2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Button Style"
                          value={
                            ['contained', 'outlined', 'text'].includes(sectionDraft.buttonVariant)
                              ? sectionDraft.buttonVariant
                              : 'outlined'
                          }
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), buttonVariant: event.target.value },
                            }))
                          }
                          disabled={!sectionDraft.buttonEnabled}
                        >
                          <MenuItem value="outlined">Outlined</MenuItem>
                          <MenuItem value="contained">Filled</MenuItem>
                          <MenuItem value="text">Text</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          size="small"
                          label="Button Background"
                          type="color"
                          fullWidth
                          value={sectionDraft.buttonBgColor || '#059669'}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), buttonBgColor: event.target.value },
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                          disabled={!sectionDraft.buttonEnabled}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          size="small"
                          label="Button Text"
                          type="color"
                          fullWidth
                          value={sectionDraft.buttonTextColor || '#059669'}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), buttonTextColor: event.target.value },
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                          disabled={!sectionDraft.buttonEnabled}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                      {availablePageLinks.map((link) => (
                        <Button
                          key={`section-editor-link-${link.path}`}
                          size="small"
                          variant={sectionDraft.buttonPath === link.path ? 'contained' : 'outlined'}
                          disabled={!sectionDraft.buttonEnabled}
                          onClick={() =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), buttonPath: link.path },
                            }))
                          }
                        >
                          {link.label}
                        </Button>
                      ))}
                    </Box>
                  </>
                )}

                {sectionDraft.type !== 'hero' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Heading Style
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Title Size:{' '}
                          {clampNumber(
                            sectionDraft.sectionTitleFontSize,
                            SECTION_TITLE_FONT_MIN,
                            SECTION_TITLE_FONT_MAX,
                            SECTION_TITLE_FONT_DEFAULT
                          )}
                          px
                        </Typography>
                        <Slider
                          size="small"
                          min={SECTION_TITLE_FONT_MIN}
                          max={SECTION_TITLE_FONT_MAX}
                          step={1}
                          value={clampNumber(
                            sectionDraft.sectionTitleFontSize,
                            SECTION_TITLE_FONT_MIN,
                            SECTION_TITLE_FONT_MAX,
                            SECTION_TITLE_FONT_DEFAULT
                          )}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                sectionTitleFontSize: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Heading Alignment</InputLabel>
                        <Select
                          label="Heading Alignment"
                          value={
                            ['left', 'center', 'right'].includes(sectionDraft.sectionTitleAlign)
                              ? sectionDraft.sectionTitleAlign
                              : 'left'
                          }
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), sectionTitleAlign: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="left">Left</MenuItem>
                          <MenuItem value="center">Center</MenuItem>
                          <MenuItem value="right">Right</MenuItem>
                        </Select>
                      </FormControl>
                      <Grid container spacing={1.2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            size="small"
                            label="Title Color"
                            type="color"
                            fullWidth
                            value={normalizeHexColor(sectionDraft.sectionTitleColor, SECTION_TITLE_COLOR_DEFAULT)}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), sectionTitleColor: event.target.value },
                              }))
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Title Hex"
                            value={sectionDraft.sectionTitleColor || SECTION_TITLE_COLOR_DEFAULT}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), sectionTitleColor: event.target.value.trim() },
                              }))
                            }
                            onBlur={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: {
                                  ...(prev.draft || {}),
                                  sectionTitleColor: normalizeHexColor(
                                    event.target.value,
                                    SECTION_TITLE_COLOR_DEFAULT
                                  ),
                                },
                              }))
                            }
                            placeholder="#0F172A"
                          />
                        </Grid>
                      </Grid>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(sectionDraft.sectionTitleBgEnabled)}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), sectionTitleBgEnabled: event.target.checked },
                              }))
                            }
                          />
                        }
                        label="Enable Title Background"
                      />
                      {Boolean(sectionDraft.sectionTitleBgEnabled) && (
                        <Grid container spacing={1.2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              size="small"
                              label="Background"
                              type="color"
                              fullWidth
                              value={normalizeHexColor(sectionDraft.sectionTitleBgColor, SECTION_TITLE_BG_DEFAULT)}
                              onChange={(event) =>
                                setSectionEditor((prev) => ({
                                  ...prev,
                                  draft: { ...(prev.draft || {}), sectionTitleBgColor: event.target.value },
                                }))
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <TextField
                              size="small"
                              fullWidth
                              label="Background Hex"
                              value={sectionDraft.sectionTitleBgColor || SECTION_TITLE_BG_DEFAULT}
                              onChange={(event) =>
                                setSectionEditor((prev) => ({
                                  ...prev,
                                  draft: { ...(prev.draft || {}), sectionTitleBgColor: event.target.value.trim() },
                                }))
                              }
                              onBlur={(event) =>
                                setSectionEditor((prev) => ({
                                  ...prev,
                                  draft: {
                                    ...(prev.draft || {}),
                                    sectionTitleBgColor: normalizeHexColor(
                                      event.target.value,
                                      SECTION_TITLE_BG_DEFAULT
                                    ),
                                  },
                                }))
                              }
                              placeholder="#F8FAFC"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Stack>
                  </Paper>
                )}

                {['student_results_grid', 'faculty', 'founders'].includes(sectionDraft.type) && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Profile Image Style
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Profile Image Shape</InputLabel>
                        <Select
                          label="Profile Image Shape"
                          value={
                            ['circle', 'rectangle', 'square', 'rounded'].includes(
                              sectionDraft.profileImageShape
                            )
                              ? sectionDraft.profileImageShape
                              : 'circle'
                          }
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), profileImageShape: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="circle">Circle</MenuItem>
                          <MenuItem value="rectangle">Rectangle</MenuItem>
                          <MenuItem value="square">Square</MenuItem>
                          <MenuItem value="rounded">Rounded Square</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Image Alignment</InputLabel>
                        <Select
                          label="Image Alignment"
                          value={
                            ['left', 'center'].includes(sectionDraft.profileImageAlign)
                              ? sectionDraft.profileImageAlign
                              : 'left'
                          }
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), profileImageAlign: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="left">Left</MenuItem>
                          <MenuItem value="center">Center</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Image Position</InputLabel>
                        <Select
                          label="Image Position"
                          value={sectionDraft.profileImageObjectPosition || 'center center'}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), profileImageObjectPosition: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="center center">Center</MenuItem>
                          <MenuItem value="left center">Left</MenuItem>
                          <MenuItem value="right center">Right</MenuItem>
                          <MenuItem value="center top">Top</MenuItem>
                          <MenuItem value="center bottom">Bottom</MenuItem>
                        </Select>
                      </FormControl>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Profile Image Size:{' '}
                          {clampNumber(
                            sectionDraft.profileImageSize,
                            PROFILE_IMAGE_SIZE_MIN,
                            PROFILE_IMAGE_SIZE_MAX,
                            PROFILE_IMAGE_SIZE_DEFAULT
                          )}
                          px
                        </Typography>
                        <Slider
                          size="small"
                          min={PROFILE_IMAGE_SIZE_MIN}
                          max={PROFILE_IMAGE_SIZE_MAX}
                          step={1}
                          value={clampNumber(
                            sectionDraft.profileImageSize,
                            PROFILE_IMAGE_SIZE_MIN,
                            PROFILE_IMAGE_SIZE_MAX,
                            PROFILE_IMAGE_SIZE_DEFAULT
                          )}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                profileImageSize: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'dropdown' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Dropdown Options
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Placeholder"
                        value={sectionDraft.dropdownPlaceholder || 'Select an option'}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), dropdownPlaceholder: event.target.value },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={4}
                        label="Options (one per line)"
                        value={normalizeDropdownOptions(sectionDraft.dropdownOptions).join('\n')}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              dropdownOptions: normalizeStringList(event.target.value, ['Option 1']),
                            },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Use <code>{'{value}'}</code> in Button Path to pass selected option in the URL.
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {(sectionDraft.type === 'table' ||
                  sectionDraft.type === 'results' ||
                  sectionDraft.type === 'custom_combo') && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Table Data
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Columns (comma, |, or new line separated)"
                        value={normalizeTableColumns(sectionDraft.tableColumns).join(' | ')}
                        onChange={(event) =>
                          setSectionEditor((prev) => {
                            const nextColumns = parseTableColumnsInput(event.target.value);
                            return {
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                tableColumns: nextColumns,
                                tableRows: normalizeTableRows(prev.draft?.tableRows, nextColumns.length),
                              },
                            };
                          })
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={5}
                        label="Rows (one row per line, cells separated by |)"
                        value={serializeTableRows(
                          normalizeTableRows(
                            sectionDraft.tableRows,
                            normalizeTableColumns(sectionDraft.tableColumns).length
                          )
                        )}
                        onChange={(event) =>
                          setSectionEditor((prev) => {
                            const columnCount = normalizeTableColumns(prev.draft?.tableColumns).length;
                            return {
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                tableRows: parseTableRowsInput(event.target.value, columnCount),
                              },
                            };
                          })
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'image_grid' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Image Grid Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Grid Columns: {clampNumber(sectionDraft.galleryColumns, 2, 6, 3)}
                        </Typography>
                        <Slider
                          size="small"
                          min={2}
                          max={6}
                          step={1}
                          value={clampNumber(sectionDraft.galleryColumns, 2, 6, 3)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                galleryColumns: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={5}
                        label="Image URLs (one per line)"
                        value={normalizeStringList(sectionDraft.galleryImages, []).join('\n')}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              galleryImages: normalizeStringList(event.target.value, []),
                            },
                          }))
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'slider' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Slider Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={sectionDraft.sliderAutoPlay !== false}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), sliderAutoPlay: event.target.checked },
                              }))
                            }
                          />
                        }
                        label="Autoplay"
                      />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Autoplay Interval: {clampNumber(sectionDraft.sliderInterval, 1200, 10000, 3500)}ms
                        </Typography>
                        <Slider
                          size="small"
                          min={1200}
                          max={10000}
                          step={100}
                          value={clampNumber(sectionDraft.sliderInterval, 1200, 10000, 3500)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                sliderInterval: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                          disabled={sectionDraft.sliderAutoPlay === false}
                        />
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={5}
                        label="Slide Image URLs (one per line)"
                        value={normalizeStringList(sectionDraft.galleryImages, []).join('\n')}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              galleryImages: normalizeStringList(event.target.value, []),
                            },
                          }))
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {(sectionDraft.type === 'icon_grid' || sectionDraft.type === 'experience_story') && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {sectionDraft.type === 'experience_story' ? 'Support Points' : 'Icon Grid Settings'}
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      {sectionDraft.type === 'icon_grid' && (
                        <>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            label="Card Style"
                            value={sectionDraft.iconCardVariant === 'pill' ? 'pill' : 'card'}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), iconCardVariant: event.target.value },
                              }))
                            }
                          >
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="pill">Pill</MenuItem>
                          </TextField>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Card Corner Radius: {clampNumber(sectionDraft.iconCardRadius, 0, 64, 16)}px
                            </Typography>
                            <Slider
                              size="small"
                              min={0}
                              max={64}
                              step={1}
                              value={clampNumber(sectionDraft.iconCardRadius, 0, 64, 16)}
                              onChange={(_event, value) =>
                                setSectionEditor((prev) => ({
                                  ...prev,
                                  draft: {
                                    ...(prev.draft || {}),
                                    iconCardRadius: Array.isArray(value) ? value[0] : value,
                                  },
                                }))
                              }
                            />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Grid Columns: {clampNumber(sectionDraft.iconGridColumns, 1, 4, 3)}
                            </Typography>
                            <Slider
                              size="small"
                              min={1}
                              max={4}
                              step={1}
                              value={clampNumber(sectionDraft.iconGridColumns, 1, 4, 3)}
                              onChange={(_event, value) =>
                                setSectionEditor((prev) => ({
                                  ...prev,
                                  draft: {
                                    ...(prev.draft || {}),
                                    iconGridColumns: Array.isArray(value) ? value[0] : value,
                                  },
                                }))
                              }
                            />
                          </Box>
                        </>
                      )}
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={5}
                        label={
                          sectionDraft.type === 'experience_story'
                            ? 'Support Points (one per line: icon | title | content)'
                            : 'Cards (one per line: icon | title | content)'
                        }
                        value={serializeIconCards(sectionDraft.iconCards)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              iconCards: parseIconCardsInput(event.target.value),
                            },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        {sectionDraft.type === 'experience_story'
                          ? 'Example: • | Global Perspective | Students learn through diverse classrooms and broad worldviews.'
                          : 'Example: 🎯 | Personalized Attention | Small batches with close mentoring.'}
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'careers_openings' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Openings List Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="Layout"
                        value={
                          ['grid', 'vertical'].includes(sectionDraft.careersLayout)
                            ? sectionDraft.careersLayout
                            : 'grid'
                        }
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), careersLayout: event.target.value },
                          }))
                        }
                      >
                        <MenuItem value="grid">Grid</MenuItem>
                        <MenuItem value="vertical">Vertical</MenuItem>
                      </TextField>
                      {(sectionDraft.careersLayout || 'grid') === 'grid' && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Grid Columns: {clampNumber(sectionDraft.careersColumns, 1, 4, 3)}
                          </Typography>
                          <Slider
                            size="small"
                            min={1}
                            max={4}
                            step={1}
                            value={clampNumber(sectionDraft.careersColumns, 1, 4, 3)}
                            onChange={(_event, value) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: {
                                  ...(prev.draft || {}),
                                  careersColumns: Array.isArray(value) ? value[0] : value,
                                },
                              }))
                            }
                          />
                        </Box>
                      )}
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={6}
                        label="Openings (role | department | location | type | applyPath | imageUrl | summary)"
                        value={serializeCareerCards(sectionDraft.careerCards)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              careerCards: parseCareerCardsInput(event.target.value),
                            },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Example: Mathematics Teacher | Academics | Main Campus | Full Time | /contact | https://image-url | Teach middle and senior grades.
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'social_links' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Social Links Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={5}
                        label="Links (one per line: label | url | icon)"
                        value={serializeSocialLinks(sectionDraft.socialLinks)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              socialLinks: parseSocialLinksInput(event.target.value),
                            },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Example: Instagram | https://instagram.com/yourschool | ig
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'subscribe' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Subscribe Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Email Placeholder"
                        value={sectionDraft.subscribePlaceholder || 'Enter your email'}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), subscribePlaceholder: event.target.value },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Button Label"
                        value={sectionDraft.subscribeButtonLabel || 'Subscribe'}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), subscribeButtonLabel: event.target.value },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Success Message"
                        value={sectionDraft.subscribeSuccessMessage || 'Subscribed successfully.'}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), subscribeSuccessMessage: event.target.value },
                          }))
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'student_results_grid' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Student Results Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Grid Columns: {clampNumber(sectionDraft.resultsGridColumns, 1, 4, 3)}
                        </Typography>
                        <Slider
                          size="small"
                          min={1}
                          max={4}
                          step={1}
                          value={clampNumber(sectionDraft.resultsGridColumns, 1, 4, 3)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                resultsGridColumns: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="Metric Display"
                        value={
                          ['percentage', 'marks', 'both'].includes(sectionDraft.resultsMetricMode)
                            ? sectionDraft.resultsMetricMode
                            : 'both'
                        }
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), resultsMetricMode: event.target.value },
                          }))
                        }
                      >
                        <MenuItem value="percentage">Percentage</MenuItem>
                        <MenuItem value="marks">Marks</MenuItem>
                        <MenuItem value="both">Both</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={6}
                        label="Students (one per line: name | class | percentage | marks | imageUrl | note)"
                        value={serializeResultCards(sectionDraft.resultCards)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              resultCards: parseResultCardsInput(event.target.value),
                            },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Example: Aarav Sharma | Grade 10 | 98.2% | 491/500 | https://... | School Topper
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'faculty' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Faculty Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Grid Columns: {clampNumber(sectionDraft.facultyColumns, 1, 4, 3)}
                        </Typography>
                        <Slider
                          size="small"
                          min={1}
                          max={4}
                          step={1}
                          value={clampNumber(sectionDraft.facultyColumns, 1, 4, 3)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                facultyColumns: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={6}
                        label="Faculty (one per line: name | role | imageUrl | bio)"
                        value={serializeProfileCards(sectionDraft.facultyCards)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              facultyCards: parseFacultyCardsInput(event.target.value),
                            },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Example: Ms. Priya Sharma | Mathematics Faculty | https://... | Concept-first learning approach.
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'founders' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Founders Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Grid Columns: {clampNumber(sectionDraft.founderColumns, 1, 4, 2)}
                        </Typography>
                        <Slider
                          size="small"
                          min={1}
                          max={4}
                          step={1}
                          value={clampNumber(sectionDraft.founderColumns, 1, 4, 2)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                founderColumns: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={6}
                        label="Founders (one per line: name | role | imageUrl | bio)"
                        value={serializeProfileCards(sectionDraft.founderCards)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              founderCards: parseFounderCardsInput(event.target.value),
                            },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Example: Dr. Arvind Rao | Founder | https://... | Mission-driven school leadership.
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {(sectionDraft.type === 'profile_showcase' || sectionDraft.type === 'news_highlights') && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {sectionDraft.type === 'news_highlights'
                        ? 'News Highlights Settings'
                        : 'Profile Showcase Settings'}
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Grid Columns: {clampNumber(sectionDraft.profileColumns, 1, 4, 4)}
                        </Typography>
                        <Slider
                          size="small"
                          min={1}
                          max={4}
                          step={1}
                          value={clampNumber(sectionDraft.profileColumns, 1, 4, 4)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                profileColumns: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Card Corner Radius: {clampNumber(sectionDraft.profileCardRadius, 0, 64, 22)}px
                        </Typography>
                        <Slider
                          size="small"
                          min={0}
                          max={64}
                          step={1}
                          value={clampNumber(sectionDraft.profileCardRadius, 0, 64, 22)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                profileCardRadius: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <Grid container spacing={1.2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Card Background"
                            value={sectionDraft.profileCardBgColor || '#FFFFFF'}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), profileCardBgColor: event.target.value },
                              }))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Card Border Color"
                            value={sectionDraft.profileCardBorderColor || 'rgba(15,23,42,0.12)'}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), profileCardBorderColor: event.target.value },
                              }))
                            }
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={6}
                        label={
                          sectionDraft.type === 'news_highlights'
                            ? 'News Cards (one per line: headline | category | imageUrl | summary)'
                            : 'Profiles (one per line: name | role | imageUrl | bio)'
                        }
                        value={serializeProfileCards(sectionDraft.profileCards)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              profileCards: parseProfileCardsInput(event.target.value),
                            },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        {sectionDraft.type === 'news_highlights'
                          ? 'Example: Science Expo 2026 | Campus News | https://... | Students showcased robotics, AI, and sustainability projects.'
                          : 'Example: Dr. Anil Menon | Chairperson | https://... | Strategic oversight and governance guidance.'}
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'stats_strip' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Stats Strip Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Columns: {clampNumber(sectionDraft.statsColumns, 1, 4, 3)}
                        </Typography>
                        <Slider
                          size="small"
                          min={1}
                          max={4}
                          step={1}
                          value={clampNumber(sectionDraft.statsColumns, 1, 4, 3)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                statsColumns: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        label="Overlay Color (rgba/hex)"
                        value={sectionDraft.statsOverlayColor || 'rgba(15, 23, 42, 0.58)'}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), statsOverlayColor: event.target.value },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={5}
                        label="Stats (one per line: icon | value | label)"
                        value={serializeStatsItems(sectionDraft.statsItems)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), statsItems: parseStatsItemsInput(event.target.value) },
                          }))
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Example: 🌍 | 28 | Different Nationalities
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'sidebar_content' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Sidebar Layout Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Sidebar Title"
                        value={sectionDraft.sidebarTitle || ''}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), sidebarTitle: event.target.value },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={5}
                        label="Sidebar Links (one per line: label | path)"
                        value={serializeSidebarItems(sectionDraft.sidebarItems)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), sidebarItems: parseSidebarItemsInput(event.target.value) },
                          }))
                        }
                      />
                      <Grid container spacing={1.2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="CTA Title"
                            value={sectionDraft.sidebarCtaTitle || ''}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), sidebarCtaTitle: event.target.value },
                              }))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="CTA Subtitle"
                            value={sectionDraft.sidebarCtaSubtitle || ''}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), sidebarCtaSubtitle: event.target.value },
                              }))
                            }
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={4}
                        label="CTA Buttons (one per line: label | path)"
                        value={serializeSidebarItems(sectionDraft.sidebarButtons)}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), sidebarButtons: parseSidebarItemsInput(event.target.value) },
                          }))
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'image_read_more' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Read More Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Preview Limit: {clampNumber(sectionDraft.readMoreLimit, 80, 1200, 220)} chars
                        </Typography>
                        <Slider
                          size="small"
                          min={80}
                          max={1200}
                          step={10}
                          value={clampNumber(sectionDraft.readMoreLimit, 80, 1200, 220)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                readMoreLimit: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <Grid container spacing={1.2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Read More Label"
                            value={sectionDraft.readMoreLabel || 'Read More'}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), readMoreLabel: event.target.value },
                              }))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Read Less Label"
                            value={sectionDraft.readLessLabel || 'Read Less'}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), readLessLabel: event.target.value },
                              }))
                            }
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'map' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Map Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Map Embed URL"
                        value={sectionDraft.mapEmbedUrl || ''}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), mapEmbedUrl: event.target.value },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Address Text"
                        value={sectionDraft.mapAddress || ''}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), mapAddress: event.target.value },
                          }))
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {(sectionDraft.type === 'contact_form' || sectionDraft.type === 'admissions_form') && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Form Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Submit Button Label"
                        value={sectionDraft.formSubmitLabel || 'Submit'}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), formSubmitLabel: event.target.value },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Success Message"
                        value={sectionDraft.formSuccessMessage || ''}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), formSuccessMessage: event.target.value },
                          }))
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'footer' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Footer Settings
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.8 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Address"
                        value={sectionDraft.footerAddress || ''}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), footerAddress: event.target.value },
                          }))
                        }
                      />
                      <Grid container spacing={1.2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Phone"
                            value={sectionDraft.footerPhone || ''}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), footerPhone: event.target.value },
                              }))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Email"
                            value={sectionDraft.footerEmail || ''}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), footerEmail: event.target.value },
                              }))
                            }
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={3}
                        label="Quick Links (one per line)"
                        value={normalizeStringList(sectionDraft.footerQuickLinks, []).join('\n')}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              footerQuickLinks: normalizeStringList(event.target.value, []),
                            },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Copyright"
                        value={sectionDraft.footerCopyright || ''}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), footerCopyright: event.target.value },
                          }))
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {sectionDraft.type === 'custom_combo' && (
                  <Paper variant="outlined" sx={{ p: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Custom Combo Settings
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.8 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={sectionDraft.comboShowImage !== false}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), comboShowImage: event.target.checked },
                              }))
                            }
                          />
                        }
                        label="Show Image"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={sectionDraft.comboShowTable !== false}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), comboShowTable: event.target.checked },
                              }))
                            }
                          />
                        }
                        label="Show Table"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={sectionDraft.comboShowButton !== false}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), comboShowButton: event.target.checked },
                              }))
                            }
                          />
                        }
                        label="Show Button"
                      />
                    </Stack>
                  </Paper>
                )}

                <Paper variant="outlined" sx={{ p: 1.2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Media
                  </Typography>
                  <Grid container spacing={1.2} sx={{ mt: 0.2 }}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Image URL"
                        value={sectionDraft.image || ''}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), image: event.target.value },
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<ImageIcon />}
                        onClick={() => openImageDialogFor({ type: 'section-editor-image' })}
                      >
                        Image Library
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Video URL"
                        value={sectionDraft.videoUrl || ''}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), videoUrl: event.target.value },
                          }))
                        }
                        placeholder="https://example.com/video.mp4 or embed URL"
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Layout Controls
                  </Typography>
                  <Box sx={{ mt: 1.1, display: 'grid', gap: 1.1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Animation Style</InputLabel>
                      <Select
                        label="Animation Style"
                        value={
                          ['none', 'fade-up', 'zoom-in'].includes(sectionDraft.animationStyle)
                            ? sectionDraft.animationStyle
                            : 'fade-up'
                        }
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), animationStyle: event.target.value },
                          }))
                        }
                      >
                        <MenuItem value="fade-up">Fade Up</MenuItem>
                        <MenuItem value="zoom-in">Zoom In</MenuItem>
                        <MenuItem value="none">None</MenuItem>
                      </Select>
                    </FormControl>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Animation Duration: {clampNumber(sectionDraft.animationDuration, 250, 2200, 700)}ms
                      </Typography>
                      <Slider
                        size="small"
                        min={250}
                        max={2200}
                        step={50}
                        value={clampNumber(sectionDraft.animationDuration, 250, 2200, 700)}
                        onChange={(_event, value) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              animationDuration: Array.isArray(value) ? value[0] : value,
                            },
                          }))
                        }
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {sectionDraft.type === 'hero'
                          ? `Hero Height: ${clampNumber(
                              sectionDraft.heroHeight,
                              HERO_HEIGHT_MIN,
                              HERO_HEIGHT_MAX,
                              HERO_HEIGHT_DEFAULT
                            )}% of screen`
                          : `Section Width: ${clampNumber(
                              sectionDraft.sectionWidth,
                              SECTION_WIDTH_MIN,
                              SECTION_WIDTH_MAX,
                              SECTION_WIDTH_DEFAULT
                            )}%`}
                      </Typography>
                      <Slider
                        size="small"
                        min={sectionDraft.type === 'hero' ? HERO_HEIGHT_MIN : SECTION_WIDTH_MIN}
                        max={sectionDraft.type === 'hero' ? HERO_HEIGHT_MAX : SECTION_WIDTH_MAX}
                        step={1}
                        value={
                          sectionDraft.type === 'hero'
                            ? clampNumber(
                                sectionDraft.heroHeight,
                                HERO_HEIGHT_MIN,
                                HERO_HEIGHT_MAX,
                                HERO_HEIGHT_DEFAULT
                              )
                            : clampNumber(
                                sectionDraft.sectionWidth,
                                SECTION_WIDTH_MIN,
                                SECTION_WIDTH_MAX,
                                SECTION_WIDTH_DEFAULT
                              )
                        }
                        onChange={(_event, value) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: {
                              ...(prev.draft || {}),
                              ...(sectionDraft.type === 'hero'
                                ? { heroHeight: Array.isArray(value) ? value[0] : value }
                                : { sectionWidth: Array.isArray(value) ? value[0] : value }),
                            },
                          }))
                        }
                      />
                    </Box>
                    {sectionDraft.type !== 'divider' && (
                      <>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Component Top Margin:{' '}
                            {clampNumber(
                              sectionDraft.sectionMarginTop,
                              COMPONENT_MARGIN_MIN,
                              COMPONENT_MARGIN_MAX,
                              sectionDraft.type === 'hero' ? 0 : COMPONENT_MARGIN_DEFAULT
                            )}
                            px
                          </Typography>
                          <Slider
                            size="small"
                            min={COMPONENT_MARGIN_MIN}
                            max={COMPONENT_MARGIN_MAX}
                            step={2}
                            value={clampNumber(
                              sectionDraft.sectionMarginTop,
                              COMPONENT_MARGIN_MIN,
                              COMPONENT_MARGIN_MAX,
                              sectionDraft.type === 'hero' ? 0 : COMPONENT_MARGIN_DEFAULT
                            )}
                            onChange={(_event, value) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: {
                                  ...(prev.draft || {}),
                                  sectionMarginTop: Array.isArray(value) ? value[0] : value,
                                },
                              }))
                            }
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Component Bottom Margin:{' '}
                            {clampNumber(
                              sectionDraft.sectionMarginBottom,
                              COMPONENT_MARGIN_MIN,
                              COMPONENT_MARGIN_MAX,
                              sectionDraft.type === 'hero' ? 0 : COMPONENT_MARGIN_DEFAULT
                            )}
                            px
                          </Typography>
                          <Slider
                            size="small"
                            min={COMPONENT_MARGIN_MIN}
                            max={COMPONENT_MARGIN_MAX}
                            step={2}
                            value={clampNumber(
                              sectionDraft.sectionMarginBottom,
                              COMPONENT_MARGIN_MIN,
                              COMPONENT_MARGIN_MAX,
                              sectionDraft.type === 'hero' ? 0 : COMPONENT_MARGIN_DEFAULT
                            )}
                            onChange={(_event, value) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: {
                                  ...(prev.draft || {}),
                                  sectionMarginBottom: Array.isArray(value) ? value[0] : value,
                                },
                              }))
                            }
                          />
                        </Box>
                      </>
                    )}
                    {sectionDraft.type === 'hero' && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Hero Content Width:{' '}
                          {clampNumber(
                            sectionDraft.heroContentWidth,
                            HERO_CONTENT_WIDTH_MIN,
                            HERO_CONTENT_WIDTH_MAX,
                            HERO_CONTENT_WIDTH_DEFAULT
                          )}
                          %
                        </Typography>
                        <Slider
                          size="small"
                          min={HERO_CONTENT_WIDTH_MIN}
                          max={HERO_CONTENT_WIDTH_MAX}
                          step={1}
                          value={clampNumber(
                            sectionDraft.heroContentWidth,
                            HERO_CONTENT_WIDTH_MIN,
                            HERO_CONTENT_WIDTH_MAX,
                            HERO_CONTENT_WIDTH_DEFAULT
                          )}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                heroContentWidth: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                    )}
                    {sectionDraft.type === 'hero' && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Hero Media Anchor</InputLabel>
                        <Select
                          label="Hero Media Anchor"
                          value={
                            ['center center', 'center top', 'center bottom'].includes(
                              sectionDraft.imageObjectPosition
                            )
                              ? sectionDraft.imageObjectPosition
                              : 'center center'
                          }
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), imageObjectPosition: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="center center">Center</MenuItem>
                          <MenuItem value="center top">Top</MenuItem>
                          <MenuItem value="center bottom">Bottom</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {sectionDraft.type !== 'hero' && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Media Height: {clampNumber(sectionDraft.mediaHeight, MEDIA_HEIGHT_MIN, MEDIA_HEIGHT_MAX, MEDIA_HEIGHT_DEFAULT)}px
                        </Typography>
                        <Slider
                          size="small"
                          min={MEDIA_HEIGHT_MIN}
                          max={MEDIA_HEIGHT_MAX}
                          step={2}
                          value={clampNumber(
                            sectionDraft.mediaHeight,
                            MEDIA_HEIGHT_MIN,
                            MEDIA_HEIGHT_MAX,
                            MEDIA_HEIGHT_DEFAULT
                          )}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                mediaHeight: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                    )}
                    {sectionDraft.type !== 'hero' && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Section Shape</InputLabel>
                        <Select
                          label="Section Shape"
                          value={
                            ['rectangle', 'rounded'].includes(sectionDraft.sectionFrameShape)
                              ? sectionDraft.sectionFrameShape
                              : 'rectangle'
                          }
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), sectionFrameShape: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="rectangle">Sharp</MenuItem>
                          <MenuItem value="rounded">Soft Corners</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {sectionDraft.type !== 'hero' &&
                      (sectionDraft.sectionFrameShape || 'rectangle') === 'rounded' && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Section Corner Radius: {clampNumber(sectionDraft.sectionCornerRadius, 0, 40, 0)}px
                          </Typography>
                          <Slider
                            size="small"
                            min={0}
                            max={40}
                            step={1}
                            value={clampNumber(sectionDraft.sectionCornerRadius, 0, 40, 0)}
                            onChange={(_event, value) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: {
                                  ...(prev.draft || {}),
                                  sectionCornerRadius: Array.isArray(value) ? value[0] : value,
                                },
                              }))
                            }
                          />
                        </Box>
                      )}
                    {sectionDraft.type !== 'hero' && (
                      <Stack spacing={0.8}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F172A' }}>
                          Section Background
                        </Typography>
                        <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                          {SECTION_BACKGROUND_PALETTE.map((swatch) => {
                            const isActive =
                              (sectionDraft.sectionBgColor || SECTION_BG_DEFAULT).toLowerCase() ===
                              swatch.value.toLowerCase();
                            return (
                              <Button
                                key={swatch.value}
                                size="small"
                                variant={isActive ? 'contained' : 'outlined'}
                                onClick={() =>
                                  setSectionEditor((prev) => ({
                                    ...prev,
                                    draft: { ...(prev.draft || {}), sectionBgColor: swatch.value },
                                  }))
                                }
                                sx={{
                                  minWidth: 'auto',
                                  px: 0.75,
                                  py: 0.55,
                                  gap: 0.65,
                                  borderRadius: 1.6,
                                  borderColor: isActive ? swatch.value : 'rgba(15,23,42,0.12)',
                                  backgroundColor: isActive ? 'rgba(15,23,42,0.04)' : '#FFFFFF',
                                  color: '#0F172A',
                                  boxShadow: 'none',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: 0.9,
                                    backgroundColor: swatch.value,
                                    border: '1px solid rgba(15,23,42,0.12)',
                                  }}
                                />
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
                                  {swatch.label}
                                </Typography>
                              </Button>
                            );
                          })}
                        </Stack>
                        <TextField
                          size="small"
                          label="Custom Background Color"
                          type="color"
                          fullWidth
                          value={sectionDraft.sectionBgColor || SECTION_BG_DEFAULT}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), sectionBgColor: event.target.value },
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                    )}
                    {sectionDraft.type !== 'hero' && sectionDraft.type !== 'divider' && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={sectionDraft.sectionBorderEnabled !== false}
                            onChange={(event) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: { ...(prev.draft || {}), sectionBorderEnabled: event.target.checked },
                              }))
                            }
                          />
                        }
                        label="Show Component Border"
                      />
                    )}
                    {sectionDraft.type !== 'hero' && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Image Side</InputLabel>
                        <Select
                          label="Image Side"
                          value={sectionDraft.imagePosition || 'right'}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), imagePosition: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="left">Left</MenuItem>
                          <MenuItem value="right">Right</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {sectionDraft.type !== 'hero' && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Media Position</InputLabel>
                        <Select
                          label="Media Position"
                          value={sectionDraft.imageObjectPosition || 'center center'}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), imageObjectPosition: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="center center">Center</MenuItem>
                          <MenuItem value="left center">Left</MenuItem>
                          <MenuItem value="right center">Right</MenuItem>
                          <MenuItem value="center top">Top</MenuItem>
                          <MenuItem value="center bottom">Bottom</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {sectionDraft.type !== 'hero' && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Image Shape</InputLabel>
                        <Select
                          label="Image Shape"
                          value={
                            ['rectangle', 'rounded'].includes(sectionDraft.imageFrameShape)
                              ? sectionDraft.imageFrameShape
                              : 'rectangle'
                          }
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), imageFrameShape: event.target.value },
                            }))
                          }
                        >
                          <MenuItem value="rectangle">Rectangle</MenuItem>
                          <MenuItem value="rounded">Rounded Square</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {sectionDraft.type !== 'hero' &&
                      (sectionDraft.imageFrameShape || 'rectangle') === 'rounded' && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Image Corner Radius: {clampNumber(sectionDraft.imageCornerRadius, 0, 40, 0)}px
                          </Typography>
                          <Slider
                            size="small"
                            min={0}
                            max={40}
                            step={1}
                            value={clampNumber(sectionDraft.imageCornerRadius, 0, 40, 0)}
                            onChange={(_event, value) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: {
                                  ...(prev.draft || {}),
                                  imageCornerRadius: Array.isArray(value) ? value[0] : value,
                                },
                              }))
                            }
                          />
                        </Box>
                      )}
                    {sectionDraft.type !== 'hero' && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Media Width: {clampNumber(sectionDraft.imageColumnSpan, 2, 10, 5)}/12
                        </Typography>
                        <Slider
                          size="small"
                          min={2}
                          max={10}
                          step={1}
                          value={clampNumber(sectionDraft.imageColumnSpan, 2, 10, 5)}
                          onChange={(_event, value) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: {
                                ...(prev.draft || {}),
                                imageColumnSpan: Array.isArray(value) ? value[0] : value,
                              },
                            }))
                          }
                        />
                      </Box>
                    )}
                    {sectionDraft.type !== 'hero' && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          label="Border Color"
                          type="color"
                          value={sectionDraft.imageBorderColor || '#1f2937'}
                          onChange={(event) =>
                            setSectionEditor((prev) => ({
                              ...prev,
                              draft: { ...(prev.draft || {}), imageBorderColor: event.target.value },
                            }))
                          }
                          sx={{ width: 120 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Border Width: {clampNumber(sectionDraft.imageBorderWidth, 0, 12, 0)}px
                          </Typography>
                          <Slider
                            size="small"
                            min={0}
                            max={12}
                            step={1}
                            value={clampNumber(sectionDraft.imageBorderWidth, 0, 12, 0)}
                            onChange={(_event, value) =>
                              setSectionEditor((prev) => ({
                                ...prev,
                                draft: {
                                  ...(prev.draft || {}),
                                  imageBorderWidth: Array.isArray(value) ? value[0] : value,
                                },
                              }))
                            }
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </>
            ) : (
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Divider Controls
                </Typography>
                <Grid container spacing={1.2} sx={{ mt: 0.2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      size="small"
                      label="Divider Color"
                      type="color"
                      fullWidth
                      value={sectionDraft.dividerColor || '#059669'}
                      onChange={(event) =>
                        setSectionEditor((prev) => ({
                          ...prev,
                          draft: { ...(prev.draft || {}), dividerColor: event.target.value },
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Divider Style</InputLabel>
                      <Select
                        label="Divider Style"
                        value={sectionDraft.dividerStyle || 'solid'}
                        onChange={(event) =>
                          setSectionEditor((prev) => ({
                            ...prev,
                            draft: { ...(prev.draft || {}), dividerStyle: event.target.value },
                          }))
                        }
                      >
                        <MenuItem value="solid">Solid</MenuItem>
                        <MenuItem value="dashed">Dashed</MenuItem>
                        <MenuItem value="dotted">Dotted</MenuItem>
                        <MenuItem value="double">Double</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Divider Thickness: {clampNumber(sectionDraft.dividerThickness, 1, 10, 3)}px
                    </Typography>
                    <Slider
                      size="small"
                      min={1}
                      max={10}
                      step={1}
                      value={clampNumber(sectionDraft.dividerThickness, 1, 10, 3)}
                      onChange={(_event, value) =>
                        setSectionEditor((prev) => ({
                          ...prev,
                          draft: {
                            ...(prev.draft || {}),
                            dividerThickness: Array.isArray(value) ? value[0] : value,
                          },
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        borderTop: `${clampNumber(sectionDraft.dividerThickness, 1, 10, 3)}px ${
                          sectionDraft.dividerStyle || 'solid'
                        } ${sectionDraft.dividerColor || '#059669'}`,
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 1.7,
          py: 1.05,
          borderTop: '1px solid rgba(15, 23, 42, 0.08)',
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)',
        }}
      >
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 1.8, px: 1.6 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          startIcon={saving ? undefined : <SaveIcon />}
          sx={{
            borderRadius: 1.8,
            px: 1.7,
            background: 'linear-gradient(135deg, #059669 0%, #F59E0B 100%)',
            boxShadow: '0 12px 30px rgba(37,99,235,0.22)',
          }}
          disabled={saving}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Section'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SectionEditorDialog;
